/**
 * DebugPanel — 调试日志窗口（按钮 + 窗口）。
 *
 *  - 一个可拖拽移动的浮层，实时订阅 `DebugLogger` 渲染结构化条目；
 *  - 过滤：级别下限、信道（app/net）、关键字；
 *  - 操作：暂停/继续、清空、复制、下载 .log、导出**棋盘布局快照**。
 *  - 与 `--z-debug`（06-ui-integration.md:144-152）同层，恒在菜单之上、
 *    致命层之下。
 *
 * 棋盘布局数据由场景通过 `layoutProvider` 注入（解耦：面板不 import Three）。
 */
import type { LogEntry, Logger } from '@shared/debug/logger';
import { LOG_LEVELS, type LogLevel } from '@shared/debug/logger';
import { tr, type DictKey } from './i18n';
import { $settings } from '../store/settingsStore';

export interface DebugPanelHandle {
  open(): void;
  close(): void;
  toggle(): void;
  readonly isOpen: boolean;
}

export interface DebugPanelDeps {
  /** 场景提供当前棋盘布局文本快照（无场景时返回 null）。 */
  layoutProvider?: () => string | null;
  /** 场景提供当前状态摘要（回合/模式/计数等）。 */
  summaryProvider?: () => string | null;
}

const LEVELS: LogLevel[] = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'];

const LEVEL_COLOR: Record<LogLevel, string> = {
  trace: '#8a8f98',
  debug: '#7fb3d5',
  info: '#9ec87a',
  warn: '#e0b25a',
  error: '#e07a5f',
  fatal: '#e63946',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  parent?: HTMLElement
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  parent?.appendChild(e);
  return e;
}

/** 面板实例（单例）——由 main.ts 创建并注入场景引用。 */
export function createDebugPanel(
  root: HTMLElement,
  logger: Logger,
  deps: DebugPanelDeps = {}
): DebugPanelHandle {
  const wrap = el('div', 'dbg-panel', root);
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-label', 'Debug Log');
  wrap.style.display = 'none';

  let open = false;
  let paused = false;
  let minLevel: LogLevel = 'debug';
  let filterChannel = 'all';
  let keyword = '';

  // ── 标题栏 ──
  const bar = el('div', 'dbg-bar', wrap);
  const title = el('span', 'dbg-title', bar);
  title.textContent = '🐞 Debug Log';
  const version = el('span', 'dbg-ver', bar);
  version.textContent = `v${logger.version}`;
  el('span', 'dbg-grow', bar); // flex 弹性占位：把右侧按钮组推到标题栏末尾

  const btnPause = el('button', 'dbg-btn', bar);
  btnPause.type = 'button';
  const btnClear = el('button', 'dbg-btn', bar);
  btnClear.type = 'button';
  const btnCopy = el('button', 'dbg-btn', bar);
  btnCopy.type = 'button';
  const btnSnap = el('button', 'dbg-btn', bar);
  btnSnap.type = 'button';
  const btnClose = el('button', 'dbg-btn', bar);
  btnClose.type = 'button';
  btnClose.textContent = '✕';

  // ── 过滤栏 ──
  const filters = el('div', 'dbg-filters', wrap);
  const lvSel = el('select', 'dbg-select', filters) as HTMLSelectElement;
  for (const l of LEVELS) {
    const o = el('option', '', lvSel) as HTMLOptionElement;
    o.value = l;
    o.textContent = `≥ ${l}`;
  }
  lvSel.value = minLevel;

  const chSel = el('select', 'dbg-select', filters) as HTMLSelectElement;
  for (const c of ['all', 'app', 'net']) {
    const o = el('option', '', chSel) as HTMLOptionElement;
    o.value = c;
    o.textContent = c === 'all' ? 'ALL' : c;
  }
  const kw = el('input', 'dbg-input', filters) as HTMLInputElement;
  kw.placeholder = 'filter…';

  // ── 日志体 ──
  const body = el('div', 'dbg-body', wrap);
  const foot = el('div', 'dbg-foot', wrap);
  const stats = el('span', 'dbg-stats', foot);
  const layoutBox = el('pre', 'dbg-layout', wrap);
  layoutBox.style.display = 'none';

  function t(k: DictKey): string {
    return tr(k, $settings.get().language);
  }

  function refreshLabels(): void {
    btnPause.textContent = paused ? `▶ ${t('dbgResume')}` : `⏸ ${t('dbgPause')}`;
    btnClear.textContent = t('dbgClear');
    btnCopy.textContent = t('dbgCopy');
    btnSnap.textContent = t('dbgLayout');
    btnClose.title = t('dbgClose');
    stats.textContent = `${logger.entries().length} entries · ${logger.getLevel()}`;
  }
  refreshLabels();

  function visible(e: LogEntry): boolean {
    if (LOG_LEVELS[e.level] < LOG_LEVELS[minLevel]) return false;
    if (filterChannel !== 'all' && e.channel !== filterChannel) return false;
    if (keyword && !`${e.msg} ${JSON.stringify(e.fields)}`.toLowerCase().includes(keyword))
      return false;
    return true;
  }

  function renderAll(): void {
    body.replaceChildren();
    const list = logger.entries().filter(visible);
    // 仅渲染尾部（DOM 上限），完整条目走导出。
    const tail = list.slice(-400);
    for (const e of tail) body.appendChild(rowFor(e));
    stats.textContent = `${tail.length}/${logger.entries().length} shown · ${logger.getLevel()}`;
    body.scrollTop = body.scrollHeight;
  }

  function rowFor(e: LogEntry): HTMLElement {
    const r = el('div', 'dbg-row');
    const lv = el('span', 'dbg-lv', r);
    lv.textContent = e.level.toUpperCase().padEnd(5);
    lv.style.color = LEVEL_COLOR[e.level];
    const ts = el('span', 'dbg-ts', r);
    ts.textContent = new Date(e.time).toISOString().slice(11, 23);
    const ch = el('span', 'dbg-ch', r);
    ch.textContent = `[${e.channel}]`;
    const msg = el('span', 'dbg-msg', r);
    msg.textContent = e.msg;
    const fk = Object.keys(e.fields);
    if (fk.length) {
      const f = el('span', 'dbg-fields', r);
      f.textContent = JSON.stringify(e.fields);
    }
    return r;
  }

  const unsub = logger.subscribe((e) => {
    if (paused || !open) return;
    if (!visible(e)) return;
    body.appendChild(rowFor(e));
    while (body.childElementCount > 420) body.firstElementChild?.remove();
    body.scrollTop = body.scrollHeight;
    stats.textContent = `${body.childElementCount}/${logger.entries().length} shown · ${logger.getLevel()}`;
  });

  // ── 事件 ──
  btnPause.addEventListener('click', () => {
    paused = !paused;
    refreshLabels();
  });
  btnClear.addEventListener('click', () => {
    logger.clear();
    renderAll();
  });
  btnCopy.addEventListener('click', () => {
    void navigator.clipboard?.writeText(logger.snapshot()).catch(() => undefined);
  });
  btnSnap.addEventListener('click', () => {
    const show = layoutBox.style.display === 'none';
    layoutBox.style.display = show ? 'block' : 'none';
    if (show) {
      const summary = deps.summaryProvider?.() ?? '';
      const layout = deps.layoutProvider?.() ?? t('dbgNoScene');
      layoutBox.textContent = `${summary}\n\n${layout}`;
    }
  });
  btnClose.addEventListener('click', () => handle.close());
  lvSel.addEventListener('change', () => {
    minLevel = lvSel.value as LogLevel;
    renderAll();
  });
  chSel.addEventListener('change', () => {
    filterChannel = chSel.value;
    renderAll();
  });
  kw.addEventListener('input', () => {
    keyword = kw.value.trim().toLowerCase();
    renderAll();
  });

  // ── 拖拽移动（拖动标题栏） ──
  let drag: { x: number; y: number; left: number; top: number } | null = null;
  bar.addEventListener('pointerdown', (ev) => {
    if ((ev.target as HTMLElement).closest('button')) return;
    const rect = wrap.getBoundingClientRect();
    drag = { x: ev.clientX, y: ev.clientY, left: rect.left, top: rect.top };
    wrap.setPointerCapture(ev.pointerId);
  });
  bar.addEventListener('pointermove', (ev) => {
    if (!drag) return;
    wrap.style.left = `${Math.max(0, drag.left + ev.clientX - drag.x)}px`;
    wrap.style.top = `${Math.max(0, drag.top + ev.clientY - drag.y)}px`;
    wrap.style.right = 'auto';
    wrap.style.bottom = 'auto';
  });
  bar.addEventListener('pointerup', (ev) => {
    drag = null;
    wrap.releasePointerCapture?.(ev.pointerId);
  });

  const handle: DebugPanelHandle = {
    open(): void {
      open = true;
      wrap.style.display = 'flex';
      renderAll();
    },
    close(): void {
      open = false;
      wrap.style.display = 'none';
    },
    toggle(): void {
      if (open) handle.close();
      else handle.open();
    },
    get isOpen(): boolean {
      return open;
    },
  };

  // 语言切换时刷新按钮文案。
  $settings.subscribe(() => refreshLabels());

  // 供 main.ts 卸载时调用。
  (wrap as HTMLElement & { __dispose?: () => void }).__dispose = (): void => {
    unsub();
  };

  return handle;
}
